
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { CandidateForm, CandidateFormData } from './CandidateForm';
import { Edit } from 'lucide-react';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { APIError } from '@/utils/errorHandler';
import type { Tables } from '@/integrations/supabase/types';

type Candidate = Tables<'candidates'>;

interface CandidateEditDialogProps {
  candidateId: string;
  /**
   * Optional trigger element. If provided, the dialog will manage its own open
   * state when the trigger is clicked. If `open` is passed, the component acts
   * as a controlled dialog and this prop is ignored.
   */
  trigger?: React.ReactNode;
  /**
   * Controlled open state. When provided, `onClose` must also be supplied and
   * the dialog will not render a trigger button.
   */
  open?: boolean;
  /** Callback when the dialog should be closed */
  onClose?: () => void;
  /** Callback after a successful update so parent can refetch data */
  onUpdate?: () => void;
}

export function CandidateEditDialog({
  candidateId,
  trigger,
  open: controlledOpen,
  onClose,
  onUpdate,
}: CandidateEditDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler({ component: 'CandidateEditDialog' });

  const closeDialog = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
  };

  const { data: candidate, isLoading, error } = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('candidates')
          .select('*')
          .eq('id', candidateId)
          .single();

        if (error) {
          throw new APIError(
            error.message || 'Failed to fetch candidate',
            400,
            { component: 'CandidateEditDialog', action: 'fetchCandidate' }
          );
        }

        return data;
      } catch (error) {
        if (error instanceof APIError) {
          throw error;
        }
        throw new APIError(
          'Failed to load candidate data',
          500,
          { component: 'CandidateEditDialog', action: 'fetchCandidate' }
        );
      }
    },
    enabled: open,
    retry: 1,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: CandidateFormData) => {
      try {
        const updateData = {
          ...data,
          college_id: data.college_id || null,
          job_position_id: data.job_position_id || null,
          interviewer: Array.isArray(data.interviewer) && data.interviewer.length > 0 ? data.interviewer : null,
          joining_date: data.joining_date || null,
          // Normalize created_at: datetime-local gives "YYYY-MM-DDTHH:mm", convert to ISO
          created_at: data.created_at ? new Date(data.created_at).toISOString() : undefined,
          // Ensure array fields are properly formatted
          projects_details: Array.isArray(data.projects_details) ? data.projects_details.filter(p => p.trim()) : [],
          project_links: Array.isArray(data.project_links) ? data.project_links.filter(p => p.trim()) : [],
          certifications_awards: Array.isArray(data.certifications_awards) ? data.certifications_awards.filter(p => p.trim()) : [],
          event_participations: Array.isArray(data.event_participations) ? data.event_participations.filter(p => p.trim()) : [],
          internships_experience: Array.isArray(data.internships_experience) ? data.internships_experience.filter(p => p.trim()) : [],
          updated_at: new Date().toISOString(),
        };

        const { error } = await supabase
          .from('candidates')
          .update(updateData as never)
          .eq('id', candidateId);

        if (error) {
          throw new APIError(
            error.message || 'Failed to update candidate',
            400,
            { component: 'CandidateEditDialog', action: 'updateCandidate' }
          );
        }
      } catch (error) {
        if (error instanceof APIError) {
          throw error;
        }
        throw new APIError(
          'Failed to update candidate',
          500,
          { component: 'CandidateEditDialog', action: 'updateCandidate' }
        );
      }
    },
    onSuccess: () => {
      toast({
        title: "Candidate Updated",
        description: "The candidate has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['candidate', candidateId] });
      closeDialog();
      onUpdate?.();
    },
    onError: (error) => {
      handleError(error, 'updateCandidate');
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const handleSubmit = (data: CandidateFormData) => {
    setIsSubmitting(true);
    updateMutation.mutate(data);
  };

  // Handle query error
  if (error && open) {
    handleError(error, 'fetchCandidate');
  }

  return (
    <ErrorBoundary>
      <Dialog
        open={open}
        onOpenChange={(val) => {
          if (isControlled) {
            if (!val) onClose?.();
          } else {
            setInternalOpen(val);
          }
        }}
      >
        {!isControlled && (
          <DialogTrigger asChild>
            {trigger || (
              <Button variant="outline" size="sm">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </DialogTrigger>
        )}
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Candidate</DialogTitle>
            <DialogDescription>
              Update the candidate's information below.
            </DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <div className="p-8">
              <LoadingSpinner text="Loading candidate data..." />
            </div>
          ) : candidate ? (
            <CandidateForm
              candidate={candidate}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          ) : (
            <div className="p-4 text-center text-destructive">
              Failed to load candidate data. Please try again.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
}
